import { fetchCaptchaData } from '@eliadmin/core/src/api/recaptcha-fetcher';
import { CaptchaAction } from '@eliadmin/core/src/api/recaptcha-actions';

import type {
  EliProductDetail,
  ProductConf,
  ProductConfCols,
  ProductConfId,
  ProductConfKodProv,
  ProductDetail,
  ProductFolieSpec,
  ProductZadaSpec,
  ProformAddress,
  ProformFolie,
  ProformInvoice,
  ProformMaybeArray,
  ProformOrder,
  ProformOrnament,
  SetProductConf,
} from '@eliadmin/core/src/api/proform/proform-types';
import type { QuestionFormValues } from '@eliadmin/core/src/components/product/detail/tabs/product-tab-quetsion-form';
import type { StringOrNumber } from '@eliadmin/core/src/types/misc-types';

// Mapping from command names to endpoint paths
const COMMAND_TO_ENDPOINT: Record<string, string> = {
  // ELI commands
  ELI_GET_PAYMENTS: 'eli-get-payments',
  ELI_GET_PRODUCTS: 'eli-get-products',
  ELI_GET_PRODUCT: 'eli-get-products',
  ELI_ADD_ITEM_TO_BASKET: 'eli-add-item-to_basket',
  ELI_CREATE_ORDER: 'eli-create-orded',
  ELI_DELETE_BASKET: 'eli-delete-basket',
  ELI_DELETE_BASKET_ITEM: 'eli-delete-basket-item',
  ELI_EDIT_BASKET_ITEM: 'eli-edit-basket-item',
  ELI_GET_ATTACH: 'eli-get-attach',
  ELI_GET_BASKET: 'eli-get-basket',
  ELI_GET_BASKET_SHIPMENT: 'eli-get-basket_shipment',
  ELI_GET_CATEGORIES: 'eli-get-categories',
  ELI_GET_INVOICE: 'eli-get-invoice',
  ELI_GET_INVOICE_PDF: 'eli-get-invoice-pdf',
  ELI_GET_INVOICES: 'eli-get-all-invoices',
  ELI_GET_ORDER: 'eli-get-order',
  ELI_GET_ORDER_PDF: 'eli-get-order-pdf',
  ELI_GET_ORDERS: 'eli-get-all-orders',
  ELI_GET_SHIPMENTS: 'eli-get-shipments',
  ELI_GET_USER: 'eli-get-user',
  ELI_CHANGE_PASSWORD: 'eli-change-password',
  ELI_CHECK_BASKET: 'eli-check-basket',
  // Product configuration commands
  GET_PRODUCT_CONF: 'get-product-conf',
  GET_PRODUCT_CONF_COLS: 'get-product-conf-cols',
  GET_PRODUCT_CONF_FOLIE_SPEC: 'get-product-conf-folie-spec',
  GET_PRODUCT_CONF_GRP_FOLIE: 'get-product-conf-grp-folie',
  GET_PRODUCT_CONF_GRP_HRANA: 'get-product-conf-grp-hrana',
  GET_PRODUCT_CONF_GRP_PANTU: 'get-product-conf-grp-pantu',
  GET_PRODUCT_CONF_GRP_PROV: 'get-product-conf-grp-prov',
  GET_PRODUCT_CONF_GRP_SKLA: 'get-product-conf-grp-skla',
  GET_PRODUCT_CONF_GRP_ZADA: 'get-product-conf-grp-zada',
  GET_PRODUCT_CONF_HRANA: 'get-product-conf-hrana',
  GET_PRODUCT_CONF_ID: 'get-product-conf-id',
  GET_PRODUCT_CONF_KOD_FOLIE: 'get-product-conf-kod_folie',
  GET_PRODUCT_CONF_KOD_FREZOVANI: 'get-product-conf-kod-frezovani',
  GET_PRODUCT_CONF_KOD_PANTU: 'get-product-conf-kod-pantu',
  GET_PRODUCT_CONF_KOD_PROV: 'get-product-conf-kod-prov',
  GET_PRODUCT_CONF_KOD_SKLA: 'get-product-conf-kod-skla',
  GET_PRODUCT_CONF_KOD_ZADA: 'get-product-conf-kod-zada',
  GET_PRODUCT_CONF_KOVANI: 'get-product-conf-kovani',
  GET_PRODUCT_CONF_ORNAMENT: 'get-product-conf-ornament',
  GET_PRODUCT_CONF_PANT_POVRCH: 'get-product-conf-pant-povrch',
  GET_PRODUCT_CONF_PANT_SPEC: 'get-product-conf-pant-spec',
  GET_PRODUCT_CONF_PANT_UMI: 'get-product-conf-pant-umi',
  GET_PRODUCT_CONF_PATINA: 'get-product-conf-patina',
  GET_PRODUCT_CONF_SILA: 'get-product-conf-sila',
  GET_PRODUCT_CONF_SKLO_SPEC: 'get-product-conf-sklo-spec',
  GET_PRODUCT_CONF_VRTANI: 'get-product-conf-vrtani',
  GET_PRODUCT_CONF_VYKLOP: 'get-product-conf-vyklop',
  GET_PRODUCT_CONF_ZADA_SPEC: 'get-product-conf-zada-spec',
  GET_PRODUCT_NAHLED_URL: 'get-product-conf-nahled-url',
  SET_PRODUCT_CONF: 'set-product-conf',
  // Other commands
  GET_PRODUCT_DETAIL: 'get-product-detail',
  GET_USER_ADDRESS: 'get-user-address',
  SAVE_USER_ADDRESS: 'save-user-address',
  REGISTRATION: 'registration',
  SAVE_USER_INFO: 'save-user-info',
};

const DEFAULT_PROFORM_API_URL = 'http://srvol.smako.cz:20085/service';
const DEFAULT_PROFORM_TIMEOUT_MS = 15_000;

function getProformTimeoutMs() {
  const timeout = Number(process.env.PROFORM_API_TIMEOUT_MS);
  return Number.isFinite(timeout) && timeout > 0 ? timeout : DEFAULT_PROFORM_TIMEOUT_MS;
}

function getProformServiceBaseUrl() {
  const configuredUrl = process.env.PROFORM_API_URL ?? DEFAULT_PROFORM_API_URL;

  if (
    configuredUrl.includes('ZasShopServicePage.aspx') &&
    process.env.PROFORM_USE_LEGACY_API !== 'true'
  ) {
    return DEFAULT_PROFORM_API_URL;
  }

  return configuredUrl;
}

function isLegacyProformApiUrl(url = getProformServiceBaseUrl()) {
  return url.includes('ZasShopServicePage.aspx');
}

function buildLegacyProformUrl(command: string, payload: Record<string, string>) {
  const url = new URL(getProformServiceBaseUrl());
  url.searchParams.set('command', command);

  for (const [key, value] of Object.entries(payload)) {
    url.searchParams.set(key, value);
  }

  return url.toString();
}

function normalizeProformData(data: unknown) {
  if (data && typeof data === 'object' && 'root' in data) {
    return data;
  }

  return { root: data };
}

async function fetchWithTimeout(url: string, init: RequestInit) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), getProformTimeoutMs());

  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeout);
  }
}

const WINDOWS_1250_EXTENDED_CHARS =
  '\u20ac\ufffd\u201a\ufffd\u201e\u2026\u2020\u2021\ufffd\u2030\u0160\u2039\u015A\u0164\u017D\u0179' +
  '\ufffd\u2018\u2019\u201c\u201d\u2022\u2013\u2014\ufffd\u2122\u0161\u203a\u015B\u0165\u017E\u017A' +
  '\u00A0\u02C7\u02D8\u0141\u00A4\u0104\u00A6\u00A7\u00A8\u00A9\u015E\u00AB\u00AC\u00AD\u00AE\u017B' +
  '\u00B0\u00B1\u02DB\u0142\u00B4\u00B5\u00B6\u00B7\u00B8\u0105\u015F\u00BB\u013D\u02DD\u013E\u017C' +
  '\u0154\u00C1\u00C2\u0102\u00C4\u0139\u0106\u00C7\u010C\u00C9\u0118\u00CB\u011A\u00CD\u00CE\u010E' +
  '\u0110\u0143\u0147\u00D3\u00D4\u0150\u00D6\u00D7\u0158\u016E\u00DA\u0170\u00DC\u00DD\u0162\u00DF' +
  '\u0155\u00E1\u00E2\u0103\u00E4\u013A\u0107\u00E7\u010D\u00E9\u0119\u00EB\u011B\u00ED\u00EE\u010F' +
  '\u0111\u0144\u0148\u00F3\u00F4\u0151\u00F6\u00F7\u0159\u016F\u00FA\u0171\u00FC\u00FD\u0163\u02D9';

const WINDOWS_1250_BYTE_BY_CODE_POINT = new Map<number, number>(
  Array.from(WINDOWS_1250_EXTENDED_CHARS)
    .map((char, index) => [char.codePointAt(0)!, index + 0x80] as const)
    .filter(([codePoint]) => codePoint !== 0xfffd),
);

function encodeWindows1250(value: string) {
  const bytes: number[] = [];

  for (const char of value) {
    const codePoint = char.codePointAt(0)!;

    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
      continue;
    }

    bytes.push(WINDOWS_1250_BYTE_BY_CODE_POINT.get(codePoint) ?? 0x3f);
  }

  return new Uint8Array(bytes);
}

async function postProformEndpoint<TResponse>(
  command: string,
  endpoint: string,
  payload: Record<string, string>,
): Promise<TResponse> {
  const isServer = typeof window === 'undefined';
  const useLegacyApi = isServer && isLegacyProformApiUrl();
  const url = useLegacyApi
    ? buildLegacyProformUrl(command, payload)
    : isServer
      ? `${getProformServiceBaseUrl()}/${endpoint}`
      : '/api/proform';
  const body = isServer ? { command, ...payload } : { command, endpoint, payload };
  const requestBody = JSON.stringify(body);
  const sendWindows1250 = isServer && !useLegacyApi;

  const res = await fetchWithTimeout(url, {
    method: useLegacyApi ? 'GET' : 'POST',
    headers: {
      'Content-Type': sendWindows1250
        ? 'application/json; charset=windows-1250'
        : 'application/json',
    },
    body: useLegacyApi ? undefined : sendWindows1250 ? encodeWindows1250(requestBody) : requestBody,
  });

  if (!res.ok) {
    const errorText = await res.text().catch(() => '');
    throw new Error(
      `Proform API error for ${command}: ${res.status} ${res.statusText}${
        errorText ? ` - ${errorText}` : ''
      }`,
    );
  }

  const data = await res.json();
  const normalizedData = normalizeProformData(data);

  if (normalizedData?.root?.error_code) {
    throw new Error(
      `Proform API error for ${command}: ${normalizedData.root.error_code} ${normalizedData.root.error_message}`,
    );
  }

  return normalizedData as TResponse;
}

export type GeneralRequestParams = {
  session_e_mail: string;
  id_shop?: StringOrNumber;
};

export type ProformApiCommandQueryUnion =
  | {
      command: 'GET_PRODUCT_CONF_ID';
      request: GeneralRequestParams & {
        user_konf_id: StringOrNumber;
      };
      response: ProductConfId;
    }
  | {
      command: 'GET_PRODUCT_CONF_COLS';
      request: GeneralRequestParams & {
        cislo_nomenklatury: string;
      };
      response: ProductConfCols;
    }
  | {
      command: 'GET_PRODUCT_CONF';
      request: {
        session_e_mail?: string;
        id_shop?: StringOrNumber;
        cislo_nomenklatury: string;
      };
      response: ProductConf;
    }
  | {
      command: 'GET_PRODUCT_DETAIL';
      request: GeneralRequestParams & {
        cislo_nomenklatury: string;
        /** We have to explicitly pass undefined here so we don't omit the property */
        n: number | undefined;
      };
      response: ProductDetail;
    }
  | {
      command: 'GET_PRODUCT_CONF_ZADA_SPEC';
      request: GeneralRequestParams & {
        cislo_nomenklatury: string;
      };
      response: ProductZadaSpec;
    }
  | {
      command: 'GET_PRODUCT_CONF_FOLIE_SPEC';
      request: GeneralRequestParams & {
        cislo_nomenklatury: string;
      };
      response: ProductFolieSpec;
    }
  | {
      command: 'ELI_GET_PRODUCT';
      request: {
        id_shop: number;
        cislo_nomenklatury: string;
        session_akt_cenik?: string;
        cennik_poradi?: number;
      };
      response: EliProductDetail;
    }
  | {
      command: 'GET_PRODUCT_CONF_KOD_PROV';
      request: {
        id_shop: number;
        cislo_nomenklatury: string;
      };
      response: ProductConfKodProv;
    }
  | {
      command: 'GET_USER_ADDRESS';
      request: {
        session_e_mail: string;
      };
      response: ProformAddress;
    }
  | {
      command: 'GET_PRODUCT_CONF_KOD_FOLIE';
      request: {
        id_shop: number;
        cislo_nomenklatury: string;
      };
      response: ProformFolie;
    }
  | {
      command: 'GET_PRODUCT_CONF_ORNAMENT';
      request: {
        id_shop: number;
        cislo_nomenklatury: string;
      };
      response: ProformOrnament;
    }
  | {
      command: 'ELI_GET_ORDERS';
      request: {
        e_mail: string;
        od_data?: string; // Optional, format: YYYY-MM-DD
        do_data?: string; // Optional, format: YYYY-MM-DD
      };
      response: {
        root: {
          orders?: {
            order: ProformMaybeArray<ProformOrder>;
          };
        };
      };
    }
  | {
      command: 'ELI_GET_INVOICES';
      request: {
        e_mail: string;
        od_data?: string; // Optional, format: YYYY-MM-DD
        do_data?: string; // Optional, format: YYYY-MM-DD
      };
      response: {
        root: {
          invoices?: {
            invoice: ProformMaybeArray<ProformInvoice>;
          };
        };
      };
    }
  | {
      command: 'ELI_GET_INVOICE';
      request: {
        e_mail: string;
        doklad: string;
      };
      response: {
        root: {
          invoice: {
            file_name: string;
            /** file in base64 */
            att: string;
          };
        };
      };
    }
  | {
      command: 'ELI_GET_ORDER';
      request: {
        e_mail: string;
        doklad: string;
      };
      response: {
        root: {
          order: {
            file_name: string;
            /** file in base64 */
            att: string;
          };
        };
      };
    };

export type ProformApiCommand = ProformApiCommandQueryUnion['command'];
export type ProformApiCommandQuerySettings<TCommand extends ProformApiCommand = ProformApiCommand> =
  Extract<ProformApiCommandQueryUnion, { command: TCommand }>;

export async function proformApiQuery<TCommand extends ProformApiCommand = ProformApiCommand>(
  command: TCommand,
  request: ProformApiCommandQuerySettings<TCommand>['request'],
) {
  const endpoint = COMMAND_TO_ENDPOINT[command];

  if (!endpoint) {
    throw new Error(`Proform API: No endpoint mapping found for command: ${command}`);
  }

  const payload: Record<string, any> = { ...request };

  // Add special handling for GET_PRODUCT_DETAIL
  if (command === 'GET_PRODUCT_DETAIL') {
    payload['n'] = 1;
  }

  // Convert all values to strings as the API expects
  const body: Record<string, string> = {};
  for (const [key, value] of Object.entries(payload)) {
    body[key] = String(value);
  }

  return postProformEndpoint<ProformApiCommandQuerySettings<TCommand>['response']>(
    command,
    endpoint,
    body,
  );
}

// Session _e_mail              e-mail / ID uzivatele
// poradi                              poradi adresy (pokud je 0, zaklada novou, pokud je <> 0 aktualizuje stavajici)
// adresa_firma_ic             IF firmy
// adresa_nazev                  nazev firmy (splecnosti)
// adresa_jmeno                jmeno (fyzicke osoby)
// adresa_prijmeni             prijmeni (fyzicke osoby)
// adresa_firma_dic           DIC (firmy)
// adresa_firma_ic_dph    ID DPH firmy
// typ_adresy                      typ adresy (10 – dodaci adresa, 20 – fakturacni adresa)
// adresa_ulice                    adresa – ulice
// adresa_cislo_popisne   adresa – cislo popisne
// adresa_mesto                 adresa – obec
// adresa_psc                      adresa – PSC
// adresa_stat                      adresa – stat (CZ, SK, … )  ciselnik státu … prikaz GET_STATYCIS)
// uz_jednotka                    adresa - okres i na to je ciselnik … prikaz GET_UZ_JEDNOTKY_FT s parametrem stat (takze smako.cz/ZasShopServicePage.aspx/?/&command=GET_UZ_JEDNOTKY_FT&stat=CZ)
// adresa_telefon_preset  telefon – předvolba
// adresa_telefon               telefon
// adresa_mobil_preset    miobil – předvolba
// adresa_mobil

export type ProformAPiCommandMutationUnion =
  | {
      command: 'SET_PRODUCT_CONF';
      request: {
        user_konf_id: StringOrNumber;
        id_shop: StringOrNumber;
        e_mail: string;
        cislo_nomenklatury: string;
        sloupec: string;
        hodnota: string;
      };
      response: SetProductConf;
    }
  | {
      command: 'SAVE_USER_ADDRESS';
      request: {
        session_e_mail: string;
        poradi: number;

        adresa_firma_ic?: string;
        adresa_nazev?: string;
        adresa_jmeno?: string;
        adresa_prijmeni?: string;
        adresa_firma_dic?: string;
        adresa_firma_ic_dph?: string;
        typ_adresy: 10 | 20;
        adresa_ulice: string;
        adresa_cislo_popisne: string;
        adresa_mesto: string;
        adresa_psc: string;
        adresa_stat: string;
        uz_jednotka?: string;
        adresa_telefon_preset?: string;
        adresa_telefon?: string;
        adresa_mobil_preset?: string;
        adresa_mobil?: string;
      };
      response: ProformAddress;
    };

export type ProformApiCommandMutationSettings<
  TCommand extends ProformAPiCommandMutationUnion['command'] =
    ProformAPiCommandMutationUnion['command'],
> = Extract<ProformAPiCommandMutationUnion, { command: TCommand }>;
export async function proformApiMutation<
  TCommand extends ProformAPiCommandMutationUnion['command'] =
    ProformAPiCommandMutationUnion['command'],
>(command: TCommand, request: ProformApiCommandMutationSettings<TCommand>['request']) {
  const endpoint = COMMAND_TO_ENDPOINT[command];

  if (!endpoint) {
    throw new Error(`Proform API: No endpoint mapping found for command: ${command}`);
  }

  // Convert all values to strings as the API expects
  const body: Record<string, string> = {};
  for (const [key, value] of Object.entries(request)) {
    body[key] = String(value);
  }

  return postProformEndpoint<ProformApiCommandMutationSettings<TCommand>['response']>(
    command,
    endpoint,
    body,
  );
}

export const sendQuestionAboutProduct = (
  body: { text: string; customer_name: string; product: any; product_url: string; email: string },
  locale: any,
) => {
  return fetchCaptchaData<{ data: QuestionFormValues }>({
    uri: '/store/forms/product',
    method: 'POST',
    locale,
    body,
  }, CaptchaAction.ASK_QUESTION);
};

export const maybeArrayUnwrap = <T>(value: ProformMaybeArray<T>): T[] => {
  return Array.isArray(value) ? value : [value];
};
